import { useFetcher } from '@remix-run/react';

import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';

/** Real newsletter subscription endpoint, shared with the global footer. */
const SUBSCRIPTION_ACTION = '/remix-api/user/subscription';

export interface B2BSubscribeFormProps {
    placeholder: string;
    className?: string;
}

/**
 * Footer email-subscribe input. Posts to the existing newsletter endpoint
 * (`RegisterForNewsLetter`) — the same one the global footer uses — so this is
 * a working subscription, not a stub.
 *
 * Sized to fit the absolute-positioned 1920-frame layout of the spec footer
 * (Figma `517:24697` — 440×54 pill, `rgba(0,0,0,0.05)` bg, px 35 / py 19, body
 * text Neue Haas Medium 16px). Sized in `vw` so the proportions match the
 * surrounding 1920 grid; falls back to a fluid full-width pill on mobile.
 */
export const B2BSubscribeForm = ({
    placeholder,
    className,
}: B2BSubscribeFormProps) => {
    const fetcher = useFetcher<{ success?: boolean }>();
    const isSubmitting = fetcher.state !== 'idle';
    const isSuccess = fetcher.data?.success === true;

    if (isSuccess) {
        return (
            <p
                className={cn(
                    'text-[0.833vw] text-[#141414] max-md:text-sm',
                    className,
                )}
                role="status"
            >
                You&apos;re subscribed — thanks for joining us.
            </p>
        );
    }

    return (
        <fetcher.Form
            method="post"
            action={SUBSCRIPTION_ACTION}
            className={cn(
                // 440 × 54 pill at design width → 22.917vw × 2.813vw.
                // px 35 / py 19 → 1.823vw / 0.99vw.
                'flex h-[2.813vw] w-[22.917vw] items-center justify-between rounded-full bg-black/5 px-[1.823vw] py-[0.99vw]',
                'max-md:h-12 max-md:w-full max-md:px-6 max-md:py-3',
                className,
            )}
        >
            <input
                type="email"
                name="email"
                required
                placeholder={placeholder}
                autoComplete="email"
                className={cn(
                    'w-full bg-transparent font-sans text-[0.833vw] font-medium tracking-[0.02em] text-[#141414] placeholder:text-[#141414] focus:outline-none',
                    'max-md:text-sm',
                )}
            />
            <button
                type="submit"
                aria-label="Subscribe"
                disabled={isSubmitting}
                className="shrink-0 text-[#141414] transition-opacity hover:opacity-60 disabled:opacity-40"
            >
                <ArrowRight className="h-[0.686vw] w-[0.973vw] max-md:size-4" />
            </button>
        </fetcher.Form>
    );
};
